import { PermissionsDialog } from './PermissionsDialog';
import { Icon } from '@iconify/react';
import {
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useWorkspace } from '../workspace-context';
import type { AccessOverview, ManagedUser } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { Box, Grid, Stack, Typography } from '../components/mui-system';
import { GRANTED_ROLES, ROLE_LABELS, type GrantedRole } from '../../../api/src/auth/access-role';
import type { AccessGrant } from '../api/types';

export function AccessPage({ embedded = false }: { embedded?: boolean }) {
  const { user: currentUser, refreshUser } = useWorkspace();
  const [permissionsUser, setPermissionsUser] = useState<AccessGrant | null>(null);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [notice, setNotice] = useState('');
  const [overview, setOverview] = useState<AccessOverview | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState<GrantedRole>('viewer');
  const [roleSaving, setRoleSaving] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      setOverview(await api.access());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось загрузить список доступа');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    if (!editingUser) return;
    setProfileSaving(true);
    setProfileError('');
    try {
      await api.updateUser(editingUser.id, {
        displayName: displayName.trim(),
        avatarUrl: avatarUrl.trim() || null,
      });
      if (editingUser.id === currentUser.id) await refreshUser();
      await load();
      setEditingUser(null);
      setNotice('Профиль пользователя сохранён');
    } catch (caught) {
      setProfileError(caught instanceof Error ? caught.message : 'Не удалось сохранить профиль');
    } finally {
      setProfileSaving(false);
    }
  }

  async function grant(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      setError('');
      await api.grantAccess(email.trim(), role);
      setEmail('');
      setRole('viewer');
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось добавить пользователя');
    } finally {
      setSaving(false);
    }
  }

  async function changeRole(grantRow: AccessGrant, next: GrantedRole) {
    setRoleSaving(grantRow.id);
    setError('');
    try {
      await api.updateAccessRole(grantRow.id, next, grantRow.role, grantRow.permissionRevision);
      await load();
      setNotice(
        `Права ${grantRow.email}: ${ROLE_LABELS[next]}. Новая роль действует со следующего запроса, в том числе для API-токенов.`,
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось изменить роль');
    } finally {
      setRoleSaving('');
    }
  }

  async function revoke(id: string, targetEmail: string) {
    if (!window.confirm(`Закрыть доступ для ${targetEmail} и завершить его активные сессии?`))
      return;
    try {
      setError('');
      await api.revokeAccess(id);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось удалить пользователя');
    }
  }

  return (
    <Box>
      {!embedded && (
        <PageHeader
          title="Доступ к системе"
          description="Администраторы задаются окружением, а остальные Google-аккаунты управляются здесь. Закрытие доступа немедленно завершает его активные сессии."
        />
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid
        display="grid"
        gridTemplateColumns={{ xs: '1fr', lg: 'minmax(280px,.7fr) 1.3fr' }}
        gap={2}
      >
        <Card>
          <CardContent sx={{ p: 2.4 }}>
            <Stack direction="row" alignItems="center" gap={1.2} mb={2}>
              <Icon icon="solar:crown-bold-duotone" width={24} />
              <Typography variant="h3">Администраторы</Typography>
            </Stack>
            <Typography color="text.secondary" fontSize={13} mb={2.5}>
              Список администраторов задаётся в настройках сервера. Здесь можно изменить их имя и
              аватар.
            </Typography>
            <Stack gap={1} alignItems="flex-start">
              {overview?.adminEmails.map((adminEmail) => (
                <Chip key={adminEmail} label={adminEmail} color="secondary" />
              ))}
              {overview && overview.adminEmails.length === 0 && (
                <Alert severity="warning">В окружении пока нет ни одного администратора.</Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 2.4 }}>
            <Typography variant="h3" mb={0.7}>
              Разрешённые пользователи
            </Typography>
            <Typography color="text.secondary" fontSize={13} mb={3}>
              Наблюдатель может просматривать данные. Редактор также может изменять их и запускать
              операции. Роль и персональные исключения действуют во всех доступных проектах.
            </Typography>
            <Stack
              component="form"
              direction={{ xs: 'column', sm: 'row' }}
              gap={1.2}
              onSubmit={grant}
            >
              <TextField
                fullWidth
                type="email"
                label="Email пользователя"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="off"
              />
              <TextField
                select
                label="Роль нового пользователя"
                value={role}
                onChange={(event) => setRole(event.target.value as GrantedRole)}
                sx={{ minWidth: 180 }}
              >
                {GRANTED_ROLES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {ROLE_LABELS[value]}
                  </MenuItem>
                ))}
              </TextField>
              <Button type="submit" variant="contained" disabled={saving || !email.trim()}>
                {saving ? <CircularProgress size={20} /> : 'Добавить'}
              </Button>
            </Stack>

            <Stack mt={2.2} gap={0.9}>
              {overview?.allowedUsers.map((grantRow) => (
                <Stack
                  key={grantRow.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  gap={1.4}
                  sx={{ p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                >
                  <Box
                    width={38}
                    height={38}
                    display="grid"
                    sx={{ placeItems: 'center', bgcolor: 'secondary.main', borderRadius: 99 }}
                  >
                    <Icon icon="solar:user-check-bold-duotone" width={21} />
                  </Box>
                  <Box minWidth={0} flex={1}>
                    <Typography fontWeight={750} noWrap>
                      {overview.users.find((user) => user.email === grantRow.email)?.displayName ??
                        grantRow.email}
                    </Typography>
                    <Typography color="text.secondary" fontSize={11} noWrap>
                      {grantRow.email} ·{' '}
                      {overview.users.some((user) => user.email === grantRow.email)
                        ? 'Профиль создан'
                        : 'Ещё не входил'}{' '}
                      · Добавил {grantRow.createdByEmail} ·{' '}
                      {new Date(grantRow.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  {overview.adminEmails.includes(grantRow.email) ? (
                    <Chip label={ROLE_LABELS.superadmin} />
                  ) : (
                    <TextField
                      select
                      size="small"
                      label={`Роль ${grantRow.email}`}
                      value={grantRow.role}
                      disabled={Boolean(roleSaving)}
                      onChange={(event) =>
                        void changeRole(grantRow, event.target.value as GrantedRole)
                      }
                      sx={{ minWidth: 165 }}
                    >
                      {GRANTED_ROLES.map((value) => (
                        <MenuItem key={value} value={value}>
                          {ROLE_LABELS[value]}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {!overview.adminEmails.includes(grantRow.email) && (
                    <Button
                      onClick={() => setPermissionsUser(grantRow)}
                      aria-label={`Права ${grantRow.email}`}
                    >
                      Права по разделам
                    </Button>
                  )}
                  <Tooltip title="Закрыть доступ">
                    <IconButton
                      aria-label={`Закрыть доступ для ${grantRow.email}`}
                      color="error"
                      onClick={() => void revoke(grantRow.id, grantRow.email)}
                    >
                      <Icon icon="solar:trash-bin-trash-linear" width={20} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              {overview && overview.allowedUsers.length === 0 && (
                <Alert severity="info">Дополнительных пользователей пока нет.</Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h3" mb={1}>
            Профили пользователей
          </Typography>
          <Typography color="text.secondary" fontSize={13} mb={2}>
            Имя и аватар можно изменить после первого входа пользователя. Правки сохраняются при
            повторном входе через Google.
          </Typography>
          {!overview && !error && (
            <CircularProgress size={24} aria-label="Загрузка пользователей" />
          )}
          {overview?.users.length === 0 && (
            <Alert severity="info">Зарегистрированных пользователей пока нет.</Alert>
          )}
          <Stack gap={1}>
            {overview?.users.map((user) => (
              <Stack
                key={user.id}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={1.5}
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
              >
                <Avatar src={user.avatarUrl ?? undefined} alt={user.displayName}>
                  {user.displayName.slice(0, 1).toUpperCase()}
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography fontWeight={750}>{user.displayName}</Typography>
                  <Typography
                    color="text.secondary"
                    fontSize={13}
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {user.email}
                  </Typography>
                  <Typography color="text.secondary" fontSize={11}>
                    Первый вход: {new Date(user.createdAt).toLocaleString('ru')}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={user.role ? ROLE_LABELS[user.role] : 'Доступ закрыт'}
                  color={user.isAdmin ? 'secondary' : user.hasAccess ? 'success' : 'default'}
                />
                <Button
                  aria-label={`Редактировать профиль ${user.email}`}
                  onClick={() => {
                    setEditingUser(user);
                    setDisplayName(user.displayName);
                    setAvatarUrl(user.avatarUrl ?? '');
                    setProfileError('');
                  }}
                >
                  Редактировать
                </Button>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
      {permissionsUser && (
        <PermissionsDialog
          grant={permissionsUser}
          onClose={() => setPermissionsUser(null)}
          onSaved={async () => {
            await load();
            setPermissionsUser(null);
            setNotice('Права сохранены. Они действуют со следующего запроса.');
          }}
        />
      )}
      <Dialog
        open={Boolean(editingUser)}
        onClose={profileSaving ? undefined : () => setEditingUser(null)}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={saveProfile}>
          <DialogTitle>Профиль пользователя</DialogTitle>
          <DialogContent>
            <Stack gap={2} pt={1}>
              {profileError && <Alert severity="error">{profileError}</Alert>}
              <TextField
                label="Email Google-аккаунта"
                value={editingUser?.email ?? ''}
                slotProps={{ input: { readOnly: true } }}
                helperText="Email привязан к учётной записи Google."
              />
              <TextField
                label="Имя пользователя"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
                disabled={profileSaving}
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
              <TextField
                label="URL аватара"
                type="url"
                value={avatarUrl}
                onChange={(event) => setAvatarUrl(event.target.value)}
                disabled={profileSaving}
                helperText="Ссылка HTTP/HTTPS. Очистите поле, чтобы убрать аватар."
                slotProps={{ htmlInput: { maxLength: 2048 } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={profileSaving} onClick={() => setEditingUser(null)}>
              Отмена
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={profileSaving || !displayName.trim()}
            >
              {profileSaving ? 'Сохраняем…' : 'Сохранить профиль'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
